import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { RefreshCw, Phone, MapPin, Calendar, ArrowLeft, Inbox, LogOut, Users, Plus, Trash2, X, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { BUSINESS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  new: { label: "New", cls: "bg-[#FF4E00] text-white" },
  contacted: { label: "Contacted", cls: "bg-yellow-400 text-black" },
  done: { label: "Done", cls: "bg-black text-white" },
};

const inputCls = "w-full rounded-none border-2 border-black bg-transparent py-3 px-3 text-base placeholder:text-black/40 focus:outline-none focus:border-[#FF4E00] transition-colors min-h-[44px]";

function StaffPanel({ onClose }) {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await axios.get(`${API}/auth/staff`); setStaff(data); }
    catch { toast.error("Could not load staff."); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/auth/staff`, form);
      toast.success(`${form.name} can now log in.`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    try { await axios.delete(`${API}/auth/staff/${u.id}`); load(); toast.success("Removed."); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" data-testid="staff-panel" onClick={onClose}>
      <div className="h-full w-full max-w-md overflow-y-auto border-l-2 border-black bg-white p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-anton text-3xl uppercase tracking-tight">Staff Logins</h2>
          <button data-testid="staff-close" onClick={onClose} className="p-2"><X size={24} /></button>
        </div>

        <form onSubmit={add} className="mt-6 space-y-4 border-2 border-black p-4" data-testid="staff-add-form">
          <div className="font-bold uppercase tracking-wide text-sm">Add a staff member</div>
          <input data-testid="staff-name" className={inputCls} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <input data-testid="staff-email" type="email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <input data-testid="staff-password" type="text" className={inputCls} placeholder="Temporary password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          <button data-testid="staff-add-submit" disabled={saving} className="flex w-full items-center justify-center gap-2 bg-[#FF4E00] px-4 py-3 font-anton text-lg uppercase text-white disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />} Add Staff
          </button>
        </form>

        <div className="mt-6 space-y-3">
          {staff.map((u) => (
            <div key={u.id} data-testid={`staff-item-${u.id}`} className="flex items-center justify-between border-b border-black/10 py-3">
              <div>
                <div className="font-bold">{u.name} {u.role === "owner" && <span className="ml-1 bg-black px-2 py-0.5 text-xs uppercase text-white">Owner</span>}</div>
                <div className="text-sm text-muted-foreground">{u.email}</div>
              </div>
              {u.role !== "owner" && (
                <button data-testid={`staff-remove-${u.id}`} onClick={() => remove(u)} className="p-2 text-red-600 hover:bg-red-50"><Trash2 size={18} /></button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast.error("New passwords don't match."); return; }
    if (form.new_password.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password updated. Use it next time you log in.");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="password-modal" onClick={onClose}>
      <div className="w-full max-w-md border-2 border-black bg-white p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-anton text-3xl uppercase tracking-tight">Change Password</h2>
          <button data-testid="password-close" onClick={onClose} className="p-2"><X size={24} /></button>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="password-form">
          <input data-testid="password-current" type="password" className={inputCls} placeholder="Current password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} required autoComplete="current-password" />
          <input data-testid="password-new" type="password" className={inputCls} placeholder="New password (min 8 chars)" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} required autoComplete="new-password" />
          <input data-testid="password-confirm" type="password" className={inputCls} placeholder="Confirm new password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required autoComplete="new-password" />
          <button data-testid="password-submit" disabled={saving} className="flex w-full items-center justify-center gap-2 bg-black px-4 py-3 font-anton text-lg uppercase text-white transition-colors hover:bg-[#FF4E00] disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={18} /> : <KeyRound size={18} />} Update Password
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStaff, setShowStaff] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings`);
      setBookings(data);
    } catch {
      toast.error("Could not load bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cycle = async (b) => {
    const order = ["new", "contacted", "done"];
    const next = order[(order.indexOf(b.status) + 1) % order.length];
    try {
      await axios.patch(`${API}/bookings/${b.id}?status=${next}`);
      setBookings((prev) => prev.map((x) => (x.id === b.id ? { ...x, status: next } : x)));
    } catch {
      toast.error("Update failed.");
    }
  };

  const counts = bookings.reduce((a, b) => ({ ...a, [b.status]: (a[b.status] || 0) + 1 }), {});

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-background" data-testid="admin-page">
      <header className="sticky top-0 z-40 border-b border-black bg-white px-4 sm:px-8 py-4">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-3">
          <Link to="/" data-testid="admin-back-link" className="flex items-center gap-2 font-anton text-lg sm:text-xl uppercase tracking-tight">
            <ArrowLeft size={20} /> Moe's Dashboard
          </Link>
          <div className="flex items-center gap-2">
            {user?.role === "owner" && (
              <button data-testid="admin-staff-btn" onClick={() => setShowStaff(true)} className="flex items-center gap-2 border-2 border-black px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors hover:bg-black hover:text-white">
                <Users size={14} /> <span className="hidden sm:inline">Staff</span>
              </button>
            )}
            <button data-testid="admin-password-btn" onClick={() => setShowPassword(true)} className="flex items-center gap-2 border-2 border-black px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors hover:bg-black hover:text-white">
              <KeyRound size={14} /> <span className="hidden sm:inline">Password</span>
            </button>
            <button data-testid="admin-refresh" onClick={load} className="flex items-center gap-2 border-2 border-black px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors hover:bg-black hover:text-white">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 bg-[#FF4E00] px-3 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-black">
              <LogOut size={14} /> <span className="hidden sm:inline">Log Out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-4 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <h1 className="font-anton text-5xl sm:text-6xl uppercase tracking-tighter">Quote Requests</h1>
          {user && <span data-testid="admin-welcome" className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Hi, {user.name || user.email}</span>}
        </div>

        <div className="mt-6 flex flex-wrap gap-4">
          {["new", "contacted", "done"].map((s) => (
            <div key={s} data-testid={`stat-${s}`} className="border-2 border-black px-6 py-4">
              <div className="font-bebas text-5xl leading-none text-[#FF4E00]">{counts[s] || 0}</div>
              <div className="text-xs font-bold uppercase tracking-[0.15em]">{STATUS[s].label}</div>
            </div>
          ))}
          <div className="border-2 border-black bg-black px-6 py-4 text-white">
            <div className="font-bebas text-5xl leading-none">{bookings.length}</div>
            <div className="text-xs font-bold uppercase tracking-[0.15em]">Total</div>
          </div>
        </div>

        {!loading && bookings.length === 0 && (
          <div data-testid="admin-empty" className="mt-16 flex flex-col items-center gap-4 border-2 border-dashed border-black/30 py-20 text-center">
            <Inbox size={48} className="text-black/30" />
            <p className="font-anton text-2xl uppercase">No requests yet</p>
            <p className="text-muted-foreground">New quote requests from your site will land here.</p>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 gap-px lg:grid-cols-2">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`booking-item-${b.id}`} className="border-2 border-black p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-anton text-2xl uppercase tracking-tight">{b.name}</div>
                  <a href={`tel:${b.phone}`} className="mt-1 flex items-center gap-1 text-sm font-bold text-[#FF4E00]">
                    <Phone size={13} /> {b.phone}
                  </a>
                </div>
                <button
                  data-testid={`status-btn-${b.id}`}
                  onClick={() => cycle(b)}
                  className={`px-3 py-1 text-xs font-bold uppercase tracking-wide ${STATUS[b.status]?.cls || "bg-secondary"}`}
                >
                  {STATUS[b.status]?.label || b.status}
                </button>
              </div>
              <div className="mt-4 space-y-1 text-sm">
                <div className="font-bold uppercase tracking-wide">{b.service}{b.dumpster_size ? ` · ${b.dumpster_size}` : ""}</div>
                <div className="flex items-center gap-1 text-muted-foreground"><MapPin size={13} /> {b.address}</div>
                {b.preferred_date && <div className="flex items-center gap-1 text-muted-foreground"><Calendar size={13} /> {b.preferred_date}</div>}
                {b.email && <div className="text-muted-foreground">{b.email}</div>}
                {b.message && <p className="mt-2 border-l-2 border-black pl-3 text-muted-foreground">“{b.message}”</p>}
              </div>
              <div className="mt-4 text-xs uppercase tracking-wide text-black/40">
                {new Date(b.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </main>

      {showStaff && <StaffPanel onClose={() => setShowStaff(false)} />}
      {showPassword && <ChangePasswordModal onClose={() => setShowPassword(false)} />}
    </div>
  );
}
